import { MarketingCampaign } from '@principle-theorem/principle-core';
import {
  IAutomationScopeData,
  ICampaignEntryReportResult,
  ICampaignEntryResetRules,
  IMarketingCampaign,
  IMarketingCampaignEntry,
  IPatient,
  MarketingCampaignEntryStatus,
  MarketingCampaignType,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import {
  CustomRecurrenceFrequency,
  DocumentReference,
  Firestore,
  Query,
  Timestamp,
  WithRef,
  resolveWithConcurrency,
  sortByCreatedAt,
  toMoment,
  toQuery,
  toTimestamp,
  undeletedQuery,
  where,
} from '@principle-theorem/shared';
import { first, toPairs } from 'lodash';
import { unitOfTime } from 'moment-timezone';

export interface IEntryEligibility extends ICampaignEntryResetRules {
  optedOut: boolean;
  entryCount: number;
  lastEntryDate?: Timestamp;
  nextEligibleDate?: Timestamp;
  reentryId: string;
}

interface IEntryEligibilityPair {
  entry: ICampaignEntryReportResult;
  eligibility: IEntryEligibility;
}

export class CampaignEntryEligibility {
  static entryCountsTowardsReentryLimit(
    entry: WithRef<IMarketingCampaignEntry>
  ): boolean {
    const invalidEntryStatuses = [
      MarketingCampaignEntryStatus.Failed,
      MarketingCampaignEntryStatus.Unenrolled,
    ];
    const isInvalidStatus = invalidEntryStatuses.includes(entry.status);
    return !entry.deleted && !isInvalidStatus;
  }

  static async filterEligibleEntries(
    campaign: WithRef<IMarketingCampaign>,
    entries: ICampaignEntryReportResult[]
  ): Promise<ICampaignEntryReportResult[]> {
    const campaignHasEntries =
      await CampaignEntryEligibility.campaignHasAnyEntries(campaign);

    // Bound the per-entry fan-out: an all-patients report can contain tens of
    // thousands of entries, and an unbounded Promise.all of Firestore reads
    // exhausts the client request queue (resource-exhausted / offline). 50 is
    // conservative next to the enrol loop's 100 because the preview re-runs on
    // every debounced date-filter change.
    const eligibilities: IEntryEligibilityPair[] = await resolveWithConcurrency(
      entries,
      50,
      async (entry) => {
        const previousEntries = campaignHasEntries
          ? await CampaignEntryEligibility.findPreviousEntries(campaign, entry)
          : [];
        const validEntries = previousEntries.filter((previousEntry) =>
          CampaignEntryEligibility.entryCountsTowardsReentryLimit(previousEntry)
        );
        const eligibility = CampaignEntryEligibility.buildEntryEligibility(
          campaign,
          validEntries,
          entry.marketingOptIn,
          entry.scopeData,
          entry.patientRef
        );
        return { entry, eligibility };
      }
    );

    const approved = eligibilities.reduce(
      (acc: IEntryEligibilityPair[], pair) => {
        const isEligible = CampaignEntryEligibility.isEligible(
          pair.eligibility,
          acc.map((accPair) => accPair.eligibility)
        );
        if (isEligible) {
          return [...acc, pair];
        }
        return acc;
      },
      []
    );

    return approved.map((eligibility) => eligibility.entry);
  }

  static async campaignHasAnyEntries(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<boolean> {
    const existingEntry = await Firestore.firstResult(
      undeletedQuery(MarketingCampaign.marketingCampaignEntryCol(campaign))
    );
    return existingEntry !== undefined;
  }

  static buildEntryEligibility(
    campaign: WithRef<IMarketingCampaign>,
    entries: WithRef<IMarketingCampaignEntry>[],
    marketingOptIn: boolean,
    scopeData: IAutomationScopeData,
    patientRef: DocumentReference<IPatient>
  ): IEntryEligibility {
    const eligibility: IEntryEligibility = {
      ...campaign.entryResetRules,
      optedOut: false,
      entryCount: entries.length,
      lastEntryDate: first(entries.sort(sortByCreatedAt))?.createdAt,
      reentryId: CampaignEntryEligibility.getReentryId(
        campaign,
        scopeData,
        patientRef
      ),
    };

    if (campaign.type === MarketingCampaignType.Marketing) {
      eligibility.optedOut = !marketingOptIn;
    }

    if (CampaignEntryEligibility.isUnderEntryLimit(eligibility, [])) {
      eligibility.nextEligibleDate =
        CampaignEntryEligibility.calculateNextEligibleDate(eligibility);
    }

    return eligibility;
  }

  static isUnderEntryLimit(
    eligibility: IEntryEligibility,
    alreadyApproved: IEntryEligibility[]
  ): boolean {
    const entryCount = CampaignEntryEligibility.getRunningEntryCount(
      eligibility,
      alreadyApproved
    );
    if (!eligibility.multipleEntry) {
      return entryCount < 1;
    }

    const restrictToOneEntryPerRun = eligibility.frequencyCount > 0;
    const hasAlreadyApprovedAnEntryThisRun =
      CampaignEntryEligibility.getMatchingEligibilities(
        eligibility,
        alreadyApproved
      ).length > 0;
    if (restrictToOneEntryPerRun && hasAlreadyApprovedAnEntryThisRun) {
      return false;
    }

    return eligibility.maxEntryCount
      ? entryCount < eligibility.maxEntryCount
      : true;
  }

  static getMatchingEligibilities(
    eligibility: IEntryEligibility,
    alreadyApproved: IEntryEligibility[]
  ): IEntryEligibility[] {
    return alreadyApproved.filter(
      (item) => item.reentryId === eligibility.reentryId
    );
  }

  static getRunningEntryCount(
    eligibility: IEntryEligibility,
    alreadyApproved: IEntryEligibility[]
  ): number {
    return eligibility.entryCount + alreadyApproved.length;
  }

  static isPastNextEligibleDate(
    eligibility: IEntryEligibility,
    now = toTimestamp()
  ): boolean {
    if (!eligibility.nextEligibleDate) {
      return false;
    }
    return toMoment(eligibility.nextEligibleDate).isSameOrBefore(toMoment(now));
  }

  static calculateNextEligibleDate(eligibility: IEntryEligibility): Timestamp {
    if (!eligibility.lastEntryDate) {
      return toTimestamp();
    }
    const frequencyUnit = getFrequencyUnit(eligibility.frequencyType);
    const resetDate = toMoment(eligibility.lastEntryDate).add(
      eligibility.frequencyCount,
      frequencyUnit
    );
    return toTimestamp(resetDate);
  }

  static getEntriesForPatientQuery(
    campaign: WithRef<IMarketingCampaign>,
    patientRef: DocumentReference<IPatient>
  ): Query<IMarketingCampaignEntry> {
    return toQuery(
      undeletedQuery(MarketingCampaign.marketingCampaignEntryCol(campaign)),
      where(`patient.ref`, '==', patientRef)
    );
  }

  static getEntriesWithScopeQuery(
    campaign: WithRef<IMarketingCampaign>,
    scopeData: IAutomationScopeData
  ): Query<IMarketingCampaignEntry> {
    const constraints = toPairs(scopeData).map(([key, value]) =>
      where(`scopeData.${key}`, '==', value)
    );
    return toQuery(
      undeletedQuery(MarketingCampaign.marketingCampaignEntryCol(campaign)),
      ...constraints
    );
  }

  static async getEntriesWithScope(
    campaign: WithRef<IMarketingCampaign>,
    scopeData: IAutomationScopeData
  ): Promise<WithRef<IMarketingCampaignEntry>[]> {
    const scopeQuery = CampaignEntryEligibility.getEntriesWithScopeQuery(
      campaign,
      scopeData
    );
    return Firestore.getDocs(scopeQuery);
  }

  static isEligible(
    eligibility: IEntryEligibility,
    alreadyApproved: IEntryEligibility[]
  ): boolean {
    const matchingApproved = CampaignEntryEligibility.getMatchingEligibilities(
      eligibility,
      alreadyApproved
    );
    return (
      !eligibility.optedOut &&
      CampaignEntryEligibility.isPastNextEligibleDate(eligibility) &&
      CampaignEntryEligibility.isUnderEntryLimit(eligibility, matchingApproved)
    );
  }

  static getReentryId(
    campaign: WithRef<IMarketingCampaign>,
    scopeData: IAutomationScopeData,
    patientRef: DocumentReference<IPatient>
  ): string {
    const reentryScope =
      campaign.entryResetRules.reentryScope ?? campaign.scope;
    if (
      reentryScope === TemplateScope.Patient &&
      scopeData.scope === TemplateScope.Patient
    ) {
      return scopeData.patientRef.path;
    }
    if (
      reentryScope === TemplateScope.Appointment &&
      scopeData.scope === TemplateScope.Appointment
    ) {
      return scopeData.appointmentRef.path;
    }
    if (
      reentryScope === TemplateScope.Invoice &&
      scopeData.scope === TemplateScope.Invoice
    ) {
      return scopeData.invoiceRef.path;
    }
    return patientRef.path;
  }

  static async findPreviousEntries(
    campaign: WithRef<IMarketingCampaign>,
    entry: ICampaignEntryReportResult
  ): Promise<WithRef<IMarketingCampaignEntry>[]> {
    const reentryScope =
      campaign.entryResetRules.reentryScope ?? campaign.scope;
    if (reentryScope === TemplateScope.Patient) {
      const patientQuery = CampaignEntryEligibility.getEntriesForPatientQuery(
        campaign,
        entry.patientRef
      );
      return Firestore.getDocs(patientQuery);
    }
    return CampaignEntryEligibility.getEntriesWithScope(
      campaign,
      entry.scopeData
    );
  }
}

export function getFrequencyUnit(
  frequencyType: CustomRecurrenceFrequency
): unitOfTime.Diff {
  return CUSTOM_RECURRENCE_FREQUENCY_UNIT_MAP[frequencyType];
}

const CUSTOM_RECURRENCE_FREQUENCY_UNIT_MAP: {
  [key in CustomRecurrenceFrequency]: unitOfTime.Diff;
} = {
  [CustomRecurrenceFrequency.Daily]: 'days',
  [CustomRecurrenceFrequency.Weekly]: 'weeks',
  [CustomRecurrenceFrequency.Monthly]: 'months',
  [CustomRecurrenceFrequency.Yearly]: 'years',
};
